/**
 * Hooks for the Opinions (team positions) API — M1 Phase C. The read mirrors
 * ``useSystems``; the admin mutations (override / clear / recompute) invalidate
 * the whole list since a synthesis pass can rewrite several positions at once.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi, type ApiError } from "@/lib/api";
import type { SynthesizeResult, SyntheticStance, TaxonomyFeedback, TaxonomyGap, TeamPosition } from "@/lib/types";

const opinionsKey = ["opinions"] as const;
const feedbackKey = ["taxonomy-feedback"] as const;
const gapsKey = ["taxonomy-gaps"] as const;

export function useOpinions() {
  const apiCall = useApi();
  return useQuery<TeamPosition[], ApiError>({
    queryKey: opinionsKey,
    queryFn: ({ signal }) => apiCall<TeamPosition[]>("/api/opinions", { signal }),
  });
}

/** What personas (registered synthetic voices) filed, kept apart from the
 *  positions: synthesis never reads persona rows, so these render beside a
 *  position, never inside it. */
export function useSyntheticStances() {
  const apiCall = useApi();
  return useQuery<SyntheticStance[], ApiError>({
    queryKey: [...opinionsKey, "synthetic-stances"],
    queryFn: ({ signal }) => apiCall<SyntheticStance[]>("/api/opinions/synthetic-stances", { signal }),
  });
}

export interface OverrideInput {
  topicKey: string;
  decisionPointKey: string;
  decision: string;
  rationale?: string;
}

export function useSetOverride() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<TeamPosition, ApiError, OverrideInput>({
    mutationFn: ({ topicKey, decisionPointKey, decision, rationale }) =>
      apiCall<TeamPosition>(`/api/opinions/${topicKey}/${decisionPointKey}/override`, {
        method: "PUT",
        body: { decision, rationale: rationale || null },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: opinionsKey }),
  });
}

export function useClearOverride() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<TeamPosition, ApiError, { topicKey: string; decisionPointKey: string }>({
    mutationFn: ({ topicKey, decisionPointKey }) =>
      apiCall<TeamPosition>(`/api/opinions/${topicKey}/${decisionPointKey}/override`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: opinionsKey }),
  });
}

export function useSynthesizeTopic() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<SynthesizeResult, ApiError, string>({
    mutationFn: (topicKey) =>
      apiCall<SynthesizeResult>(`/api/opinions/synthesize/${topicKey}`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: opinionsKey }),
  });
}

export interface PromoteOptionInput {
  decisionPointKey: string;
  label: string;
  pros: string[];
  cons: string[];
  promotedFrom?: string;
}

/** Admin: promote a named option into the taxonomy overlay — it becomes a
 *  seeded-looking option (with pros/cons) in every future interview. */
export function usePromoteOption() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, PromoteOptionInput>({
    mutationFn: ({ decisionPointKey, label, pros, cons, promotedFrom }) =>
      apiCall(`/api/taxonomy/decision-points/${decisionPointKey}/options`, {
        method: "POST",
        body: { label, pros, cons, promoted_from: promotedFrom ?? null },
      }),
    // Option lists surface on both the opinions browse and the interview's
    // topic detail — refresh both.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opinionsKey });
      void qc.invalidateQueries({ queryKey: ["preference-topics"] });
    },
  });
}

/** Admin: the catalog-feedback queue — what interviewees said is wrong with the
 *  taxonomy itself (an option's pros/cons, its label, a decision point's
 *  framing). Enabled only for admins; the endpoint 403s for everyone else.
 *  Fetched with resolved rows included — the card splits on ``resolved_at`` so
 *  the open queue stays front and the history sits behind a toggle. */
export function useTaxonomyFeedback(enabled: boolean) {
  const apiCall = useApi();
  return useQuery<TaxonomyFeedback[], ApiError>({
    queryKey: feedbackKey,
    queryFn: ({ signal }) =>
      apiCall<TaxonomyFeedback[]>("/api/taxonomy/feedback?include_resolved=true", { signal }),
    enabled,
  });
}

/** Admin: close one feedback row once it's been acted on (usually a catalog
 *  edit, which is a code change by design). Idempotent server-side. */
export function useResolveFeedback() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (feedbackId) =>
      apiCall(`/api/taxonomy/feedback/${feedbackId}/resolve`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedbackKey }),
  });
}

/** Admin: reopen a resolved row — resolve's undo (resolving is a single
 *  un-confirmed click, so it has to be reversible). Idempotent server-side. */
export function useUnresolveFeedback() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (feedbackId) =>
      apiCall(`/api/taxonomy/feedback/${feedbackId}/unresolve`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedbackKey }),
  });
}

/** Admin: the catalog-gap queue — open questions the synthesis pass derived
 *  that no current decision point can capture. Fetched with resolved rows
 *  included; the card splits on ``resolved_at`` like the feedback card. */
export function useTaxonomyGaps(enabled: boolean) {
  const apiCall = useApi();
  return useQuery<TaxonomyGap[], ApiError>({
    queryKey: gapsKey,
    queryFn: ({ signal }) =>
      apiCall<TaxonomyGap[]>("/api/taxonomy/gaps?include_resolved=true", { signal }),
    enabled,
  });
}

/** Admin: close one gap — the topic was authored in code, or judged not worth
 *  one. Stays closed when the next pass re-derives the same gap. */
export function useResolveGap() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (gapId) => apiCall(`/api/taxonomy/gaps/${gapId}/resolve`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: gapsKey }),
  });
}

/** Admin: reopen a resolved gap — same undo contract as feedback's. */
export function useUnresolveGap() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (gapId) => apiCall(`/api/taxonomy/gaps/${gapId}/unresolve`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: gapsKey }),
  });
}
